import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Bot, Menu, Github, Sparkles, Users, TrendingUp, User, Brain, MessageCircle, X, Rocket, Wand2, ShoppingCart, Video, HelpCircle } from 'lucide-react';
import { AgentCard } from './components/AgentCard';
import { FilterSidebar } from './components/FilterSidebar';
import { ScrapeButton } from './components/ScrapeButton';
import { AIAssistant } from './components/AIAssistant'; // Keep this import
import { AgentBuilder } from './components/AgentBuilder';
import { AgentBuilderButton } from './components/AgentBuilderButton';
import { DeploymentButton } from './components/DeploymentButton';
import { MonitoringButton } from './components/MonitoringButton';
import { MarketplaceButton } from './components/MarketplaceButton';
import { TavusVideoButton } from './components/TavusVideoButton';
import { TavusVideoIntegration } from './components/TavusVideoIntegration';
import { TavusVideoHelp } from './components/TavusVideoHelp';
import { SmartRecommendations } from './components/SmartRecommendations';
import { PersonalizedDashboard } from './components/PersonalizedDashboard';
import { DeploymentWorkflow } from './components/DeploymentWorkflow';
import { MonitoringDashboard } from './components/MonitoringDashboard';
import { MarketplaceModal } from './components/MarketplaceModal';
import { AgentShareModal } from './components/AgentShareModal';
import { A2AProtocolDashboard } from './components/A2AProtocolDashboard';
import { A2ADocumentation } from './components/A2ADocumentation';
import { A2AIntegrationGuide } from './components/A2AIntegrationGuide';
import { A2AExampleProjects } from './components/A2AExampleProjects';
import { A2AUsageDashboard } from './components/A2AUsageDashboard';
import { BarChart3, Search, Filter } from './components/Icons'; // Import only necessary icons
import { useAgents } from './hooks/useAgents';
import { AuthProvider, useAuth } from './lib/AuthContext';
import { Login } from './components/Auth/Login';
import { Signup } from './components/Auth/Signup';
import { Profile } from './components/Auth/Profile';
import { ChangePassword } from './components/Auth/ChangePassword';
import { ResetPassword } from './components/Auth/ResetPassword';
import { UpdatePassword } from './components/Auth/UpdatePassword';
import { EmailConfirmationHandler } from './components/Auth/EmailConfirmationHandler';
import { ProtectedRoute } from './components/Auth/ProtectedRoute';
import { Header } from './components/Layout/Header';
import { DashboardPage } from './pages/DashboardPage'; // Import the new DashboardPage
import { LandingPage } from './pages/LandingPage';
import { TourGuide } from './components/TourGuide';

function MainApp() {
  const { agents, categories, loading, error, fetchAgents } = useAgents();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState(''); // Keep for discover view
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [assistantInitialMessage, setAssistantInitialMessage] = useState('');
  const [selectedAgentForShare, setSelectedAgentForShare] = useState<Agent | null>(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [activeView, setActiveView] = useState<'discover' | 'recommendations' | 'dashboard' | 'a2a'>('discover');
  const [activeA2ATab, setActiveA2ATab] = useState<'dashboard' | 'documentation' | 'integration' | 'examples' | 'usage'>('dashboard');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Pagination state
  const [displayedAgents, setDisplayedAgents] = useState<Agent[]>([]);
  const [visibleCount, setVisibleCount] = useState(12);
  const [hasMore, setHasMore] = useState(false);

  // Debounced filter application to prevent excessive API calls
  const [filterTimeout, setFilterTimeout] = useState<NodeJS.Timeout | null>(null);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const applyFilters = useCallback(() => {
    const filters = {
      categories: selectedCategories.length > 0 ? selectedCategories : undefined,
      sources: selectedSources.length > 0 ? selectedSources : undefined,
      search: searchQuery.trim() || undefined,
    };

    fetchAgents(filters);
  }, [selectedCategories, selectedSources, searchQuery, fetchAgents]);

  // Apply filters with debouncing for search
  useEffect(() => {
    if (filterTimeout) {
      clearTimeout(filterTimeout);
    }

    const timeout = setTimeout(() => {
      applyFilters();
    }, searchQuery ? 500 : 0); // Debounce search, immediate for other filters

    setFilterTimeout(timeout);

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [selectedCategories, selectedSources, searchQuery, applyFilters]);

  // Update displayed agents when agents change or visible count changes
  useEffect(() => {
    setDisplayedAgents(agents.slice(0, visibleCount));
    setHasMore(agents.length > visibleCount);
  }, [agents, visibleCount]);

  const handleCategoryChange = (category: string) => {
    setSelectedCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const handleSourceChange = (source: string) => {
    setSelectedSources(prev =>
      prev.includes(source)
        ? prev.filter(s => s !== source)
        : [...prev, source]
    );
  };

  const handleScrapeComplete = useCallback(() => {
    // Refresh agents after scraping
    applyFilters();
  }, [applyFilters]);

  const handleShareAgent = (agent: Agent) => {
    setSelectedAgentForShare(agent);
    setShareModalOpen(true);
  };

  const handleShowMore = () => {
    setVisibleCount(prev => prev + 12);
  };

  const stats = {
    total: agents.length,
    sources: new Set(agents.map(a => a.source)).size,
    categories: new Set(agents.map(a => a.category)).size,
    totalStars: agents.reduce((sum, a) => sum + (a.stars || 0), 0),
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      <Header onMenuClick={toggleMobileMenu} />
      <div className="flex">
        {/* Sidebar - Only show for discover view */}
        {activeView === 'discover' && (
          <FilterSidebar
            categories={categories}
            selectedCategories={selectedCategories}
            selectedSources={selectedSources}
            searchQuery={searchQuery}
            isOpen={sidebarOpen}
            onCategoryChange={handleCategoryChange}
            onSourceChange={handleSourceChange}
            onSearchChange={setSearchQuery}
            onClose={() => setSidebarOpen(false)}
          />
        )}

        {/* Mobile Menu Overlay */}
        {isMobileMenuOpen && ( // This mobile menu is for the main App component, not the DashboardPage
          <div className="fixed inset-0 bg-dark-900/60 backdrop-blur-sm z-40 lg:hidden" onClick={() => setIsMobileMenuOpen(false)}>
            <div className="fixed top-0 left-0 h-full w-3/4 max-w-xs bg-white shadow-strong p-6 z-50" onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-2">
                  <Bot className="w-6 h-6 text-primary-600" />
                  <span className="font-bold text-lg text-dark-900">Agent Store</span>
                </div>
                <button onClick={() => setIsMobileMenuOpen(false)} className="p-2 text-dark-500 hover:bg-dark-50 rounded-lg transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-2">
                <button
                  onClick={() => {
                    setActiveView('discover');
                    setIsMobileMenuOpen(false);
                  }}
                  className={`flex items-center gap-3 w-full px-4 py-3 rounded-xl font-medium transition-all duration-200 ${
                    activeView === 'discover' ? 'bg-primary-100 text-primary-700 shadow-soft' : 'text-dark-700 hover:bg-dark-50'
                  }`}
                >
                  <Github className="w-5 h-5" />
                  Discover Agents
                </button>
                
                <button
                  onClick={() => {
                    setActiveView('recommendations');
                    setIsMobileMenuOpen(false);
                  }}
                  className={`flex items-center gap-3 w-full px-4 py-3 rounded-xl font-medium transition-all duration-200 ${
                    activeView === 'recommendations' ? 'bg-accent-100 text-accent-700 shadow-soft' : 'text-dark-700 hover:bg-dark-50'
                  }`}
                >
                  <Brain className="w-5 h-5" />
                  Smart Recommendations
                </button>
                
                <button
                  onClick={() => {
                    setActiveView('dashboard');
                    setIsMobileMenuOpen(false);
                  }}
                  className={`flex items-center gap-3 w-full px-4 py-3 rounded-xl font-medium transition-all duration-200 ${
                    activeView === 'dashboard' ? 'bg-primary-100 text-primary-700 shadow-soft' : 'text-dark-700 hover:bg-dark-50'
                  }`}
                >
                  <User className="w-5 h-5" />
                  Your Dashboard
                </button>
                
                <button
                  onClick={() => {
                    setActiveView('a2a');
                    setIsMobileMenuOpen(false);
                  }}
                  className={`flex items-center gap-3 w-full px-4 py-3 rounded-xl font-medium transition-all duration-200 ${
                    activeView === 'a2a' ? 'bg-accent-100 text-accent-700 shadow-soft' : 'text-dark-700 hover:bg-dark-50'
                  }`}
                >
                  <MessageCircle className="w-5 h-5" />
                  A2A Protocol
                </button>
                
                <div className="border-t border-dark-200 my-4"></div>
                
                <button
                  onClick={() => {
                    setMarketplaceOpen(true);
                    setIsMobileMenuOpen(false);
                  }}
                  className="flex items-center gap-2 w-full px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-100"
                >
                  <ShoppingCart className="w-5 h-5" />
                  Marketplace
                </button>
                
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 min-h-screen">
          {/* Content */}
          <main className="p-4 md:p-6">
            {/* Navigation Tabs */}
            <div className="mb-6 flex items-center gap-1 bg-white rounded-xl p-1 shadow-soft border border-dark-200 overflow-x-auto scrollbar-hide">
              <button
                onClick={() => setActiveView('discover')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${ // This is the main navigation for the App component
                  activeView === 'discover'
                    ? 'bg-primary-600 text-white shadow-medium'
                    : 'text-dark-600 hover:text-dark-900 hover:bg-dark-50'
                }`}
              >
                <Github className="w-4 h-4" />
                <span className="hidden sm:inline">Discover</span> Agents
              </button>
              <button
                onClick={() => setActiveView('recommendations')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  activeView === 'recommendations'
                    ? 'bg-accent-600 text-white shadow-medium'
                    : 'text-dark-600 hover:text-dark-900 hover:bg-dark-50'
                }`}
              >
                <Brain className="w-4 h-4" />
                <span className="hidden sm:inline">Smart</span> Recommendations
              </button>
              <button
                onClick={() => setActiveView('dashboard')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  activeView === 'dashboard'
                    ? 'bg-primary-600 text-white shadow-medium'
                    : 'text-dark-600 hover:text-dark-900 hover:bg-dark-50'
                }`}
              >
                <User className="w-4 h-4" />
                <span className="hidden sm:inline">Your</span> Dashboard
              </button>
              <button
                onClick={() => setActiveView('a2a')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  activeView === 'a2a'
                    ? 'bg-accent-600 text-white shadow-medium'
                    : 'text-dark-600 hover:text-dark-900 hover:bg-dark-50'
                }`}
              >
                <MessageCircle className="w-4 h-4" />
                A2A Protocol
              </button>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
              <h2 className="text-xl md:text-2xl font-bold text-dark-900">
                {activeView === 'discover' ? 'Discover AI Agents' : 
                 activeView === 'recommendations' ? 'Smart Recommendations' : // This will be moved to DashboardPage
                 activeView === 'dashboard' ? 'Your Dashboard' :
                 'Agent-to-Agent Protocol'}
              </h2>
              
              <div className="flex flex-wrap items-center gap-2 md:gap-4">
                <div className="flex flex-wrap gap-2">
                  <ScrapeButton onScrapeComplete={handleScrapeComplete} />
                </div>
              </div>
            </div>

            {/* A2A Protocol Tabs - Only show for A2A view */}
            {activeView === 'a2a' && (
              <div className="mb-6 flex items-center gap-1 bg-white rounded-xl p-1 shadow-soft border border-dark-200 overflow-x-auto scrollbar-hide">
                <button
                  onClick={() => setActiveA2ATab('dashboard')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    activeA2ATab === 'dashboard'
                      ? 'bg-accent-600 text-white shadow-medium'
                      : 'text-accent-600 hover:text-accent-800 hover:bg-accent-50'
                  }`}
                >
                  Dashboard
                </button>
                <button
                  onClick={() => setActiveA2ATab('documentation')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    activeA2ATab === 'documentation'
                      ? 'bg-accent-600 text-white shadow-medium'
                      : 'text-accent-600 hover:text-accent-800 hover:bg-accent-50'
                  }`}
                >
                  Docs
                </button>
                <button
                  onClick={() => setActiveA2ATab('integration')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    activeA2ATab === 'integration'
                      ? 'bg-accent-600 text-white shadow-medium'
                      : 'text-accent-600 hover:text-accent-800 hover:bg-accent-50'
                  }`}
                >
                  Integration
                </button>
                <button
                  onClick={() => setActiveA2ATab('examples')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    activeA2ATab === 'examples'
                      ? 'bg-accent-600 text-white shadow-medium'
                      : 'text-accent-600 hover:text-accent-800 hover:bg-accent-50'
                  }`}
                >
                  Examples
                </button>
                <button
                  onClick={() => setActiveA2ATab('usage')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    activeA2ATab === 'usage'
                      ? 'bg-accent-600 text-white shadow-medium'
                      : 'text-accent-600 hover:text-accent-800 hover:bg-accent-50'
                  }`}
                >
                  Usage
                </button>
              </div>
            )}

            {/* Stats - Only show for discover view */}
            {activeView === 'discover' && (
              <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
                <div className="card-modern p-4 md:p-5">
                  <div className="flex items-center gap-2 md:gap-3">
                    <Bot className="w-6 h-6 md:w-8 md:h-8 text-primary-600" />
                    <div>
                      <p className="text-lg md:text-2xl font-bold text-dark-900">{formatNumber(stats.total)}</p>
                      <p className="text-xs md:text-sm text-dark-600 font-medium">Total Agents</p>
                    </div>
                  </div>
                </div>

                <div className="card-modern p-4 md:p-5">
                  <div className="flex items-center gap-2 md:gap-3">
                    <Github className="w-6 h-6 md:w-8 md:h-8 text-accent-600" />
                    <div>
                      <p className="text-lg md:text-2xl font-bold text-dark-900">{stats.sources}</p>
                      <p className="text-xs md:text-sm text-dark-600 font-medium">Sources</p>
                    </div>
                  </div>
                </div>

                <div className="card-modern p-4 md:p-5">
                  <div className="flex items-center gap-2 md:gap-3">
                    <Sparkles className="w-6 h-6 md:w-8 md:h-8 text-primary-600" />
                    <div>
                      <p className="text-lg md:text-2xl font-bold text-dark-900">{stats.categories}</p>
                      <p className="text-xs md:text-sm text-dark-600 font-medium">Categories</p>
                    </div>
                  </div>
                </div>

                <div className="card-modern p-4 md:p-5">
                  <div className="flex items-center gap-2 md:gap-3">
                    <TrendingUp className="w-6 h-6 md:w-8 md:h-8 text-accent-600" />
                    <div>
                      <p className="text-lg md:text-2xl font-bold text-dark-900">{formatNumber(stats.totalStars)}</p>
                      <p className="text-xs md:text-sm text-dark-600 font-medium">Total Stars</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeView === 'discover' && (
              <>
                {error && (
                  <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-xl text-red-700">
                    <p className="font-semibold">Error loading agents:</p>
                    <p className="text-sm">{error}</p>
                    <p className="text-sm mt-2">
                      Please make sure you've connected to Supabase and the database is properly configured.
                    </p>
                  </div>
                )}

                {/* Mobile Search and Filter Bar */}
                <div className="lg:hidden mb-4">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setSidebarOpen(true)}
                      className="p-2 bg-white rounded-lg border border-gray-200 text-gray-600"
                    >
                      <Filter className="w-5 h-5" />
                    </button>
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search agents..."
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                {loading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                    {[...Array(6)].map((_, i) => (
                      <div key={i} className="card-modern p-4 md:p-6 animate-pulse">
                        <div className="h-4 bg-dark-200 rounded mb-4" />
                        <div className="h-3 bg-dark-200 rounded mb-2" />
                        <div className="h-3 bg-dark-200 rounded mb-4 w-2/3" />
                        <div className="flex gap-2 mb-4">
                          <div className="h-6 bg-dark-200 rounded w-16" />
                          <div className="h-6 bg-dark-200 rounded w-20" />
                        </div>
                        <div className="h-10 bg-dark-200 rounded" />
                      </div>
                    ))}
                  </div>
                ) : agents.length === 0 ? (
                  <div className="text-center py-12">
                    <Bot className="w-16 h-16 text-dark-400 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-dark-900 mb-2">
                      {error ? "Error loading agents" : "No agents found"}
                    </h3>
                    <p className="text-dark-600 mb-6">
                      {error 
                        ? "There was an error loading agents. Please check your database connection."
                        : searchQuery || selectedCategories.length > 0 || selectedSources.length > 0
                        ? "Try adjusting your filters or search query."
                        : "Click 'Discover New Agents' to start scraping AI agents from various sources, or create your own custom agent."
                      }
                    </p>
                    <div className="flex flex-wrap items-center justify-center gap-4">
                      {!error && <ScrapeButton onScrapeComplete={handleScrapeComplete} />}
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Results summary */}
                    <div className="mb-6 flex items-center justify-between">
                      <div className="text-sm text-dark-600 font-medium">
                        Showing {Math.min(visibleCount, agents.length)} of {agents.length} agent{agents.length !== 1 ? 's' : ''}
                        {(selectedCategories.length > 0 || selectedSources.length > 0 || searchQuery) && (
                          <span className="ml-1">
                            with current filters
                          </span>
                        )}
                      </div>
                      
                      {(selectedCategories.length > 0 || selectedSources.length > 0 || searchQuery) && (
                        <button
                          onClick={() => {
                            setSearchQuery('');
                            setSelectedCategories([]);
                            setSelectedSources([]);
                          }}
                          className="text-sm text-primary-600 hover:text-primary-700 font-medium hover:bg-primary-50 px-3 py-1 rounded-lg transition-all duration-200"
                        >
                          Clear all filters
                        </button>
                      )}
                    </div>

                    {/* Agent Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                      {displayedAgents.map((agent) => (
                        <AgentCard 
                          key={agent.id} 
                          agent={agent} 
                          onShareAgent={() => handleShareAgent(agent)}
                        />
                      ))}
                    </div>

                    {/* Show More Button */}
                    {hasMore && (
                      <div className="mt-8 text-center">
                        <button
                          onClick={handleShowMore}
                          className="btn-primary pulse-on-hover"
                        >
                          Show More Agents
                        </button>
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            {activeView === 'recommendations' && (
              <SmartRecommendations />
            )}

            {activeView === 'dashboard' && (
              <PersonalizedDashboard />
            )}

            {activeView === 'a2a' && (
              <>
                {activeA2ATab === 'dashboard' && <A2AProtocolDashboard />}
                {activeA2ATab === 'documentation' && <A2ADocumentation />}
                {activeA2ATab === 'integration' && (
                  <A2AIntegrationGuide 
                    onOpenAssistant={() => setAssistantOpen(true)}
                    onSetAssistantInitialMessage={setAssistantInitialMessage}
                  />
                )}
                {activeA2ATab === 'examples' && <A2AExampleProjects />}
                {activeA2ATab === 'usage' && <A2AUsageDashboard />}
              </>
            )}
          </main>
        </div>
      </div>

      {/* AI Assistant */}
      <AIAssistant 
        isOpen={assistantOpen} 
        onToggle={() => setAssistantOpen(!assistantOpen)}
        initialMessage={assistantInitialMessage}
        setInitialMessage={setAssistantInitialMessage}
      />

      {/* Tour Guide */}
      <TourGuide 
        onOpenAssistant={() => setAssistantOpen(true)}
        onSetAssistantInitialMessage={setAssistantInitialMessage}
      />

      {/* Agent Share Modal */}
      {selectedAgentForShare && (
        <AgentShareModal
          agent={selectedAgentForShare}
          isOpen={shareModalOpen}
          onClose={() => {
            setShareModalOpen(false);
            setSelectedAgentForShare(null);
          }}
        />
      )}
    </div>
  );
}

function AppContent() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // If user is not authenticated, show the landing page
  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/update-password" element={<UpdatePassword />} />
        <Route path="/confirm" element={<EmailConfirmationHandler />} />
        <Route path="/" element={<LandingPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  // If user is authenticated, show the main app
  return (
    <Routes>
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route path="/signup" element={<Navigate to="/" replace />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/update-password" element={<UpdatePassword />} />
      <Route path="/confirm" element={<EmailConfirmationHandler />} />
      <Route path="/profile" element={
        <ProtectedRoute>
          <Profile />
        </ProtectedRoute>
      } />
      <Route path="/change-password" element={
        <ProtectedRoute>
          <ChangePassword />
        </ProtectedRoute>
      } />
      <Route path="/" element={<DashboardPage />} />
      <Route path="/discover" element={<MainApp />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;